/**
 * «Ταμείο» → καρτέλα «Προσωπικό» (απόφαση ιδιοκτήτη 6/10/2026): οι «Πληρωμές Προσωπικού» και οι
 * «Εισπράξεις χρηστών» του Ζ σε ΜΙΑ οθόνη, μία κάρτα ανά ΑΤΟΜΟ.
 *
 * Χρήματα — ίδια ποσά με τις παλιές «Πληρωμές Προσωπικού»: GET /orders/:storeId/staff-payment-summary
 * (ταμίας → Take Away/παραλαβή που χτύπησε · σερβιτόρος → πληρωμές τραπεζιών που πέρασε · διανομέας →
 * οι διανομές του). Ο ίδιος υπάλληλος έρχεται σε δύο λίστες όταν κάνει δύο δουλειές (π.χ. ο χρήστης
 * «Ταμειο» περνά πληρωμές τραπεζιών ΚΑΙ χτυπά Take Away): συγχωνεύεται με το id του σε μία κάρτα με
 * υπο-ενότητες «Τραπέζια» / «Take Away» / «Διανομές».
 *
 * «Ενέργειες» — από την αναφορά ταμείου (`staffReport.users`, ίδιο άτομο = ίδιο employeeId): κεράσματα,
 * εκπτώσεις που ΕΚΑΝΕ, ακυρώσεις, ακυρώσεις πληρωμών, γύροι παραγγελιών. ΠΡΟΣΟΧΗ: η «Έκπτωση στις
 * εισπράξεις» της κάρτας είναι άλλο πράγμα — η έκπτωση πάνω στους λογαριασμούς/παραγγελίες που ΕΙΣΕΠΡΑΞΕ,
 * όποιος κι αν την έκανε. Όποιος έχει μόνο ενέργειες (π.χ. σερβιτόρος που έστειλε γύρους) παίρνει κι αυτός
 * κάρτα. «Χωρίς χρήστη» του Ζ → γραμμή ελέγχου δίπλα στο «Χωρίς οδηγό» και στις πλατφόρμες.
 *
 * Χωρίς staffReport (παλιό backend, ή όσο φορτώνει η αναφορά) → ακριβώς οι «Πληρωμές Προσωπικού».
 * Ενσωματώνεται χωρίς πλαίσιο modal (το «Ταμείο» δίνει το πλάτος και κυλά το σώμα της καρτέλας)·
 * αντικατέστησε το παλιό παράθυρο DriverPaymentSummary (6/10/2026).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Banknote, CreditCard, User, Package, RefreshCw, Truck, Utensils, ShoppingBag, Globe, Smartphone,
  ChevronDown, ChevronUp, Clock, Printer, Bike, HandCoins, Gift, Percent, Ban, Undo2, ListOrdered, UserX,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { API_BASE_URL } from '../config/api';
import type { CountValue, RegisterReport, UserRow } from '../../types/registerReport';

export interface StaffPanelProps {
  storeId: number;
  /** Από την αναφορά ταμείου: report.users / report.unassigned / report.userTotals / report.grossTotal — μπορεί να λείπει. */
  staffReport?: Pick<RegisterReport, 'users' | 'unassigned' | 'userTotals' | 'grossTotal'> | null;
  /** Αλλάζει όταν πατηθεί ανανέωση στο «Ταμείο» → το panel ξαναφορτώνει τα δικά του δεδομένα. */
  refreshKey?: number;
}

// ── Συμβόλαιο του GET /orders/:storeId/staff-payment-summary (backend/routes/orderAnalytics.js) ──────

interface StaffOrder {
  id: number;
  order_code?: string;
  session_id?: number;
  timestamp: string;
  /** NUMERIC της βάσης → φτάνει ως κείμενο ("12.50"): πάντα μέσω Number(). */
  amount: number | string;
  subtotal?: number | string;
  discount_amount?: number | string;
  discount_percent?: number | string;
  payment_type: string;
  customer_name?: string;
  customer_phone?: string;
  delivery_address?: string;
  platform_name?: string;
  table_number?: string;
  is_training?: boolean;
}

// Driver has different payment breakdown (prepaid/cash/pos)
interface DriverMember {
  id: number;
  name: string;
  role: 'driver';
  totalOrders: number;
  prepaidTotal: number;
  prepaidOrders: number;
  cashTotal: number;
  cashOrders: number;
  posTotal: number;
  posOrders: number;
  toCollectTotal: number;
  grandTotal: number;
  // Platform breakdown
  efoodTotal: number;
  efoodOrders: number;
  woltTotal: number;
  woltOrders: number;
  boxTotal: number;
  boxOrders: number;
  ownTotal: number;
  ownOrders: number;
  // Per-platform payment type breakdown
  efoodCash: number;
  efoodPos: number;
  efoodPrepaid: number;
  woltCash: number;
  woltPos: number;
  woltPrepaid: number;
  boxCash: number;
  boxPos: number;
  boxPrepaid: number;
  ownCash: number;
  ownPos: number;
  ownPrepaid: number;
}

// Waiter/Cashier have cash/card/iris breakdown
interface StaffMember {
  id: number;
  name: string;
  role: 'waiter' | 'cashier';
  totalOrders: number;
  trainingOrders?: number;
  cashTotal: number;
  cashOrders: number;
  cashTrainingTotal?: number;
  cashTrainingOrders?: number;
  cardTotal: number;
  cardTrainingTotal?: number;
  cardOrders: number;
  cardTrainingOrders?: number;
  irisTotal: number;
  irisOrders: number;
  // «Επί πιστώσει» (κωδ. 5) — λείπει από backend πριν την προσθήκη του.
  creditTotal?: number;
  creditOrders?: number;
  grandTotal: number;
  grandTrainingTotal?: number;
  /** Έκπτωση πάνω σε ό,τι ΕΙΣΕΠΡΑΞΕ (λογαριασμοί/παραγγελίες) — όχι «εκπτώσεις που έκανε». */
  discountTotal?: number;
  discountOrders?: number;
}

interface DriverTotals {
  totalOrders: number;
  prepaidTotal: number;
  prepaidOrders: number;
  cashTotal: number;
  cashOrders: number;
  posTotal: number;
  posOrders: number;
  toCollectTotal: number;
  grandTotal: number;
  efoodTotal: number;
  efoodOrders: number;
  woltTotal: number;
  woltOrders: number;
  boxTotal: number;
  boxOrders: number;
  ownTotal: number;
  ownOrders: number;
  // Per-platform payment type breakdown
  efoodCash: number;
  efoodPos: number;
  efoodPrepaid: number;
  woltCash: number;
  woltPos: number;
  woltPrepaid: number;
  boxCash: number;
  boxPos: number;
  boxPrepaid: number;
  ownCash: number;
  ownPos: number;
  ownPrepaid: number;
}

interface CategoryTotals {
  totalOrders: number;
  trainingOrders?: number;
  cashTotal: number;
  cashOrders: number;
  cardTotal: number;
  cardTrainingTotal?: number;
  cardOrders: number;
  irisTotal: number;
  irisOrders: number;
  creditTotal?: number;
  creditOrders?: number;
  grandTotal: number;
  grandTrainingTotal?: number;
  discountTotal?: number;
  discountOrders?: number;
}

// Platform-courier deliveries (non-self-delivery efood/wolt/box): no driver,
// no cashier — surfaced here so their (mostly prepaid) amounts are visible.
interface PlatformDelivery {
  platform: string; // 'efood' | 'wolt' | 'box'
  totalOrders: number;
  prepaidTotal: number;
  prepaidOrders: number;
  cashTotal: number;
  cashOrders: number;
  posTotal: number;
  posOrders: number;
  grandTotal: number;
}

interface PlatformDeliveryTotals {
  totalOrders: number;
  prepaidTotal: number;
  prepaidOrders: number;
  cashTotal: number;
  cashOrders: number;
  posTotal: number;
  posOrders: number;
  grandTotal: number;
}

// Delivery orders the store settles (not platform-courier) that never got a
// driver assigned — control bucket so the report reconciles with the register.
interface NoDriverBucket {
  totalOrders: number;
  prepaidTotal: number;
  cashTotal: number;
  posTotal: number;
  irisTotal: number;
  grandTotal: number;
}

interface StaffSummaryData {
  businessDayStart: string;
  endOfDay: string;
  drivers: DriverMember[];
  waiters: StaffMember[];
  cashiers: StaffMember[];
  platformDeliveries?: PlatformDelivery[];
  noDriver?: NoDriverBucket;
  totals: {
    drivers: DriverTotals;
    waiters: CategoryTotals;
    cashiers: CategoryTotals;
    platformDeliveries?: PlatformDeliveryTotals;
  };
}

// «Επί πιστώσει» μιας κατηγορίας (σερβιτόροι/ταμίες): το σύνολο του server αν υπάρχει,
// αλλιώς το άθροισμα των γραμμών (το συμβόλαιο εγγυάται το πεδίο ανά γραμμή).
const creditTotalOf = (totals: CategoryTotals | undefined, rows: StaffMember[]): number =>
  totals?.creditTotal ?? rows.reduce((s, r) => s + (r.creditTotal || 0), 0);

// ── Άτομα: συγχώνευση των τριών λιστών + «Ενέργειες» από την αναφορά ταμείου ─────────────────────────

export type StaffRoleKey = 'waiter' | 'cashier' | 'driver';
type Filter = 'all' | 'cashiers' | 'waiters' | 'drivers';

/** Σειρά υπο-ενοτήτων στην κάρτα: «Τραπέζια», «Take Away», «Διανομές». */
const ROLE_ORDER: StaffRoleKey[] = ['waiter', 'cashier', 'driver'];
const FILTERS: Filter[] = ['all', 'cashiers', 'waiters', 'drivers'];
const FILTER_ROLE: Record<Exclude<Filter, 'all'>, StaffRoleKey> = { cashiers: 'cashier', waiters: 'waiter', drivers: 'driver' };

export interface StaffPerson {
  /** employees.id — το κλειδί της συγχώνευσης (ποτέ το όνομα). */
  id: number;
  name: string;
  waiter?: StaffMember;
  cashier?: StaffMember;
  driver?: DriverMember;
  /** Η γραμμή του στην αναφορά ταμείου (Ζ/Χ), αν υπάρχει. */
  user?: UserRow;
  /** Ρόλοι για τα φίλτρα και τον υπότιτλο, με τη σειρά του ROLE_ORDER. */
  roles: StaffRoleKey[];
}

type ActionKey = 'treats' | 'discounts' | 'cancellations' | 'voids';
const ACTION_KEYS: ActionKey[] = ['treats', 'discounts', 'cancellations', 'voids'];
const countValue = (b?: CountValue | null) => ({ count: Number(b?.count) || 0, value: Number(b?.value) || 0 });

/** Οι μη μηδενικές «Ενέργειες» ενός ατόμου (κεράσματα, εκπτώσεις που έκανε, ακυρώσεις, ακυρώσεις πληρωμών). */
export function actionRowsOf(u?: UserRow | null): Array<{ key: ActionKey; count: number; value: number }> {
  if (!u) return [];
  return ACTION_KEYS
    .map(key => ({ key, ...countValue(u[key]) }))
    .filter(r => r.count > 0 || Math.abs(r.value) > 0.005);
}

const roundsOf = (u?: UserRow | null) => Number(u?.rounds) || 0;
const collectedOf = (u?: UserRow | null) => Number(u?.collected) || 0;

const isRole = (r: unknown): r is StaffRoleKey => r === 'waiter' || r === 'cashier' || r === 'driver';

/**
 * Μία εγγραφή ανά υπάλληλο: οι γραμμές σερβιτόρου/ταμία/διανομέα του ίδιου id ενώνονται και η γραμμή
 * του Ζ κολλά στο ίδιο id. Όποιος είναι μόνο στο Ζ (ενέργειες/γύροι, ή εισπράξεις που δεν βρήκαν θέση
 * στις πληρωμές προσωπικού) παίρνει δική του εγγραφή. Πρώτα όσοι έχουν χρήματα (μεγαλύτερο σύνολο πρώτο),
 * μετά οι υπόλοιποι αλφαβητικά.
 */
export function mergeStaffPeople(data: StaffSummaryData | null, users?: UserRow[] | null): StaffPerson[] {
  const byId = new Map<number, StaffPerson>();
  const personFor = (id: number, name: string): StaffPerson => {
    let p = byId.get(id);
    if (!p) {
      p = { id, name, roles: [] };
      byId.set(id, p);
    }
    return p;
  };
  if (data) {
    (data.waiters || []).forEach(w => { personFor(Number(w.id), w.name).waiter = w; });
    (data.cashiers || []).forEach(c => { personFor(Number(c.id), c.name).cashier = c; });
    (data.drivers || []).forEach(d => { personFor(Number(d.id), d.name).driver = d; });
  }
  (Array.isArray(users) ? users : []).forEach(u => {
    if (u == null || u.employeeId == null) return; // «Χωρίς χρήστη» → γραμμή ελέγχου, όχι κάρτα
    const id = Number(u.employeeId);
    const existing = byId.get(id);
    if (existing) {
      existing.user = u;
      return;
    }
    const active = Math.abs(collectedOf(u)) > 0.005 || actionRowsOf(u).length > 0 || roundsOf(u) > 0;
    if (active) byId.set(id, { id, name: u.name, user: u, roles: [] });
  });

  const people = Array.from(byId.values());
  people.forEach(p => {
    const set = new Set<StaffRoleKey>();
    ROLE_ORDER.forEach(r => { if (p[r]) set.add(r); });
    (Array.isArray(p.user?.roles) ? p.user!.roles! : []).forEach(r => { if (isRole(r)) set.add(r); });
    // Χωρίς κανέναν ρόλο αλλά με ενέργειες/γύρους: όλες είναι πράξεις τραπεζιού → «Σερβιτόροι».
    if (set.size === 0 && (actionRowsOf(p.user).length > 0 || roundsOf(p.user) > 0)) set.add('waiter');
    p.roles = ROLE_ORDER.filter(r => set.has(r));
  });

  const hasMoney = (p: StaffPerson) => !!(p.waiter || p.cashier || p.driver);
  const money = (p: StaffPerson) =>
    (Number(p.waiter?.grandTotal) || 0) + (Number(p.cashier?.grandTotal) || 0) + (Number(p.driver?.grandTotal) || 0);
  return people.sort((a, b) => {
    const am = hasMoney(a);
    const bm = hasMoney(b);
    if (am !== bm) return am ? -1 : 1;
    if (am) {
      const d = money(b) - money(a);
      if (Math.abs(d) > 0.005) return d;
    }
    return a.name.localeCompare(b.name, 'el');
  });
}

// ── Εκτύπωση (θερμικός, 42 στήλες, ετικέτες χωρίς τόνους όπως το υπόλοιπο χαρτάκι) ──────────────────

export type PrintCmd = { type: string; content: string };
const PRINT_WIDTH = 42;
const euroPrint = (n: unknown) => `${(Number(n) || 0).toFixed(2).replace('.', ',')}€`;
const printRow = (label: string, value: string, bold = false): PrintCmd =>
  ({ type: bold ? 'bold' : 'text', content: `${label}${value.padStart(PRINT_WIDTH - label.length)}` });

const PRINT_ACTION_LABELS: Record<ActionKey, string> = {
  treats: 'Κερασματα',
  discounts: 'Εκπτωσεις που εκανε',
  cancellations: 'Ακυρωσεις',
  voids: 'Ακυρωσεις πληρωμων',
};
const PRINT_SECTION_TITLES: Record<StaffRoleKey, string> = { waiter: 'ΤΡΑΠΕΖΙΑ', cashier: 'TAKE AWAY', driver: 'ΔΙΑΝΟΜΕΣ' };

/** «Ενέργειες» για το χαρτάκι του ατόμου — μόνο οι μη μηδενικές γραμμές, και οι γύροι όταν υπάρχουν. */
export function actionPrintCommands(u?: UserRow | null): PrintCmd[] {
  const rows = actionRowsOf(u);
  const rounds = roundsOf(u);
  if (rows.length === 0 && rounds <= 0) return [];
  const out: PrintCmd[] = [{ type: 'divider', content: '' }, { type: 'bold', content: 'ΕΝΕΡΓΕΙΕΣ' }];
  rows.forEach(r => out.push(printRow(`${PRINT_ACTION_LABELS[r.key]} (${r.count}):`, euroPrint(r.value))));
  if (rounds > 0) out.push(printRow('Γυροι παραγγελιων:', String(rounds)));
  return out;
}

const PREPAID_TYPES = ['online', 'prepaid', 'card_online', 'credit_card'];
const payTypeOf = (o: StaffOrder) => (o.payment_type || '').toLowerCase();
const amountOf = (o: StaffOrder) => Number(o.amount) || 0;

/** Τρόπος πληρωμής στο χαρτάκι (ελληνικά, όπως πριν). */
const paymentPrintLabel = (type: string) => {
  const lower = type?.toLowerCase() || '';
  if (lower === 'cash') return 'Μετρητά';
  if (lower === 'card' || lower === 'pos') return 'POS';
  if (lower === 'iris') return 'IRIS';
  if (lower === 'credit') return 'Επι πιστωσει';
  if (PREPAID_TYPES.includes(lower)) return 'Prepaid';
  return type;
};

export interface PersonPrintSection {
  role: StaffRoleKey;
  member: StaffMember | DriverMember;
  orders: StaffOrder[];
}

/**
 * Το χαρτάκι «ΤΑΜΕΙΟ ΧΡΗΣΤΗ» ενός ατόμου. Με έναν ρόλο: ακριβώς όπως πριν (κινήσεις, ανάλυση ανά
 * πλατφόρμα, σύνολα, ΓΕΝΙΚΟ ΣΥΝΟΛΟ) και από κάτω οι «ΕΝΕΡΓΕΙΕΣ». Με περισσότερους: μία ενότητα ανά ρόλο
 * με δικό της «Συνολο» και στο τέλος ΓΕΝΙΚΟ ΣΥΝΟΛΟ = το άθροισμά τους.
 */
export function buildPersonPrintCommands(o: {
  name: string;
  storeName: string;
  now: string;
  roleLabels: string[];
  sections: PersonPrintSection[];
  user?: UserRow | null;
  includeTraining: boolean;
  formatOrderTime: (timestamp: string) => string;
}): PrintCmd[] {
  const cmds: PrintCmd[] = [];
  const addRow = (label: string, value: string, bold = false) => { cmds.push(printRow(label, value, bold)); };
  const divider = () => { cmds.push({ type: 'divider', content: '' }); };

  cmds.push({ type: 'bold_center', content: 'ΤΑΜΕΙΟ ΧΡΗΣΤΗ' });
  cmds.push({ type: 'center', content: o.storeName });
  cmds.push({ type: 'bold_center', content: o.name });
  if (o.roleLabels.length > 0) cmds.push({ type: 'center', content: o.roleLabels.join(' / ') });
  cmds.push({ type: 'center', content: `Εκτ: ${o.now}` });
  divider();

  const printOrderLine = (order: StaffOrder) => {
    const time = o.formatOrderTime(order.timestamp);
    const code = order.order_code ? `#${order.order_code}` : order.session_id ? `Σεσ.${order.session_id}` : '';
    const line = `${time} ${code} ${paymentPrintLabel(order.payment_type)}`.substring(0, 28);
    const amount = euroPrint(amountOf(order));
    cmds.push({ type: 'text', content: `${line}${amount.padStart(PRINT_WIDTH - line.length)}` });
    const discount = Number(order.discount_amount) || 0;
    if (discount > 0) cmds.push({ type: 'text', content: `  Εκπτωση: -${euroPrint(discount)}` });
  };

  const multi = o.sections.length > 1;
  let grand = 0;
  o.sections.forEach((section, index) => {
    const isDriver = section.role === 'driver';
    if (multi) {
      if (index > 0) divider();
      cmds.push({ type: 'bold', content: PRINT_SECTION_TITLES[section.role] });
    }
    const realOrders = section.orders.filter(x => !x.is_training);
    const trainingOrders = o.includeTraining ? section.orders.filter(x => x.is_training) : [];

    realOrders.forEach(printOrderLine);
    if (trainingOrders.length > 0) {
      divider();
      cmds.push({ type: 'bold_center', content: `ΕΚΠΑΙΔΕΥΤΙΚΕΣ (${trainingOrders.length})` });
      trainingOrders.forEach(printOrderLine);
    }

    divider();
    addRow('Παραγγελιες:', `${realOrders.length}`);
    const discountTotal = isDriver ? 0 : Number((section.member as StaffMember).discountTotal) || 0;
    if (discountTotal > 0) addRow('Εκπτωσεις:', `-${euroPrint(discountTotal)}`);

    // Platform breakdown
    const platformLabels: Record<string, string> = {
      efood: 'e-food',
      box: 'Box',
      wolt: 'Wolt',
      internal: isDriver ? 'Ιδια αποστ.' : 'Ταμειο',
    };
    const isCash = (x: StaffOrder) => payTypeOf(x) === 'cash';
    const isCard = (x: StaffOrder) => ['card', 'pos'].includes(payTypeOf(x));
    const platformTotals = realOrders.reduce<Record<string, { total: number; count: number; cash: number; card: number }>>((acc, x) => {
      const p = (x.platform_name || 'internal').toLowerCase();
      if (!acc[p]) acc[p] = { total: 0, count: 0, cash: 0, card: 0 };
      acc[p].total += amountOf(x);
      acc[p].count += 1;
      if (isCash(x)) acc[p].cash += amountOf(x);
      else if (isCard(x)) acc[p].card += amountOf(x);
      return acc;
    }, {});
    const platformKeys = Object.keys(platformTotals).filter(p => platformTotals[p].count > 0);
    if (platformKeys.length > 1) {
      divider();
      cmds.push({ type: 'bold', content: 'Αναλυση ανα πλατφορμα:' });
      platformKeys.forEach(p => {
        const pt = platformTotals[p];
        addRow(`${platformLabels[p] || p} (${pt.count}):`, euroPrint(pt.total), true);
        if (pt.cash > 0) addRow('  Μετρητα:', euroPrint(pt.cash));
        if (pt.card > 0) addRow('  Καρτα:', euroPrint(pt.card));
        const other = pt.total - pt.cash - pt.card;
        if (other > 0.005) addRow('  Αλλο:', euroPrint(other));
      });
    }

    if (trainingOrders.length > 0) {
      const trainingCash = trainingOrders.reduce((s, x) => s + (isCash(x) ? amountOf(x) : 0), 0);
      if (trainingCash > 0) addRow('Εκπαιδ. Μετρ.:', euroPrint(trainingCash));
      addRow('Εκπαιδ. Παρ.:', `${trainingOrders.length}`);
    }

    // Grand totals footer
    const sumOf = (pred: (x: StaffOrder) => boolean) => realOrders.filter(pred).reduce((s, x) => s + amountOf(x), 0);
    const totalCash = sumOf(isCash);
    const totalCard = sumOf(isCard);
    const totalIris = sumOf(x => payTypeOf(x) === 'iris');
    const totalCredit = sumOf(x => payTypeOf(x) === 'credit');
    const totalPrepaid = sumOf(x => PREPAID_TYPES.includes(payTypeOf(x)));
    divider();
    if (totalCash > 0) addRow('Συνολο Μετρητων:', euroPrint(totalCash));
    if (totalCard > 0) addRow('Συνολο Καρτων:', euroPrint(totalCard));
    if (totalIris > 0) addRow('Συνολο IRIS:', euroPrint(totalIris));
    if (totalCredit > 0) addRow('Συνολο Επι πιστωσει:', euroPrint(totalCredit));
    // Ο διανομέας δεν κρατά τα προπληρωμένα: φαίνονται χωριστά και από κάτω το «Προς εισπραξη».
    if (isDriver && totalPrepaid > 0) addRow('Συνολο Prepaid:', euroPrint(totalPrepaid));
    const total = Number(section.member.grandTotal) || 0;
    grand += total;
    addRow(multi ? 'Συνολο:' : 'ΓΕΝΙΚΟ ΣΥΝΟΛΟ:', euroPrint(total), true);
    if (isDriver) addRow('Προς εισπραξη:', euroPrint((section.member as DriverMember).toCollectTotal), true);
  });

  if (multi) {
    divider();
    addRow('ΓΕΝΙΚΟ ΣΥΝΟΛΟ:', euroPrint(grand), true);
  }
  if (o.sections.length === 0) {
    // Μόνο στην αναφορά ταμείου: ενέργειες/γύροι, ή εισπράξεις που δεν φαίνονται στις πληρωμές προσωπικού.
    const z = collectedOf(o.user);
    if (Math.abs(z) > 0.005) addRow('Εισπραξεις στο Ζ:', euroPrint(z), true);
    else cmds.push({ type: 'text', content: 'Χωρις εισπραξεις' });
  }
  cmds.push(...actionPrintCommands(o.user));
  cmds.push({ type: 'feed', content: '' });
  cmds.push({ type: 'cut', content: '' });
  return cmds;
}

// ── Μορφοποίηση ──────────────────────────────────────────────────────────────────────────────────────

const currencyFormat = new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' });
const formatCurrency = (amount: unknown) => currencyFormat.format(Number(amount) || 0);

const formatTime = (isoString: string) =>
  new Date(isoString).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const formatOrderTime = (timestamp: string) =>
  new Date(timestamp).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });

// Πλέγμα πλακιδίων: όσα χωρούν στο πλάτος της κάρτας (2–4), όχι όσα «χωρούν» στην οθόνη.
const TILE_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] gap-2';
const CONTROL_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(5.5rem,1fr))] gap-2 text-center';
const CARD_GRID = 'grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4 items-start';

const ACTION_META: Record<ActionKey, { labelKey: string; icon: React.ElementType; cls: string }> = {
  treats: { labelKey: 'analytics:staffSummary.actionTreats', icon: Gift, cls: 'text-pink-600 dark:text-pink-400' },
  discounts: { labelKey: 'analytics:staffSummary.actionDiscounts', icon: Percent, cls: 'text-red-600 dark:text-red-400' },
  cancellations: { labelKey: 'analytics:staffSummary.actionCancellations', icon: Ban, cls: 'text-orange-600 dark:text-orange-400' },
  voids: { labelKey: 'analytics:staffSummary.actionVoids', icon: Undo2, cls: 'text-amber-700 dark:text-amber-400' },
};

const StaffPanel: React.FC<StaffPanelProps> = ({ storeId, staffReport, refreshKey = 0 }) => {
  const { t } = useTranslation(['analytics', 'common', 'delivery']);
  const { store } = useAuth();
  const [data, setData] = useState<StaffSummaryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [staffOrders, setStaffOrders] = useState<StaffOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [includeTraining, setIncludeTraining] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [printingKey, setPrintingKey] = useState<string | null>(null);
  const printMenuRef = useRef<HTMLDivElement>(null);
  // Μόνο η τελευταία απάντηση μετρά (γρήγορες ανανεώσεις / άλλο άτομο ενώ φορτώνει το προηγούμενο).
  const summaryReq = useRef(0);
  const ordersReq = useRef(0);

  const storeName = store?.name || 'Κατάστημα';

  const fetchData = useCallback(async (withTraining: boolean) => {
    if (!storeId) return;
    const req = ++summaryReq.current;
    ordersReq.current += 1;
    setLoading(true);
    setError(null);
    setExpandedKey(null);
    setStaffOrders([]);
    setLoadingOrders(false);

    try {
      const url = withTraining
        ? `/orders/${storeId}/staff-payment-summary?includeTraining=true`
        : `/orders/${storeId}/staff-payment-summary`;
      const response = await api.get(url);
      if (req !== summaryReq.current) return;
      setData(response.data);
    } catch (err: any) {
      if (req !== summaryReq.current) return;
      console.error('Error fetching staff summary:', err);
      setError(err?.response?.data?.message || t('analytics:staffSummary.loadError'));
    } finally {
      if (req === summaryReq.current) setLoading(false);
    }
  }, [storeId, t]);

  // Φόρτωμα: άνοιγμα, άλλο κατάστημα, ανανέωση του «Ταμείου» (refreshKey), εναλλαγή εκπαιδευτικών.
  useEffect(() => {
    fetchData(includeTraining);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, refreshKey, includeTraining]);

  useEffect(() => () => {
    summaryReq.current += 1;
    ordersReq.current += 1;
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target as Node)) {
        setShowPrintMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 5 πατήματα στην ανανέωση = εναλλαγή «με εκπαιδευτικές» (κρυφό, όπως πάντα).
  const handleRefreshClick = () => {
    const next = refreshCount + 1;
    if (next >= 5) {
      setRefreshCount(0);
      setIncludeTraining(v => !v); // το effect ξαναφορτώνει
    } else {
      setRefreshCount(next);
      fetchData(includeTraining);
    }
  };

  const users = staffReport?.users;
  const people = useMemo(() => mergeStaffPeople(data, Array.isArray(users) ? users : null), [data, users]);
  // «Χωρίς χρήστη» του Ζ: γραμμή ελέγχου όταν έχει εισπράξεις ή ενέργειες.
  const noUser = useMemo(() => {
    const u = Array.isArray(users) ? users.find(x => x && x.employeeId == null) : undefined;
    return u && (Math.abs(collectedOf(u)) > 0.005 || actionRowsOf(u).length > 0) ? u : null;
  }, [users]);

  const peopleFor = (f: Filter) => (f === 'all' ? people : people.filter(p => p.roles.includes(FILTER_ROLE[f])));
  const visiblePeople = peopleFor(filter);

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'driver': return t('delivery:roleDriver');
      case 'waiter': return t('delivery:roleWaiter');
      case 'cashier': return t('delivery:roleCashier');
      default: return role;
    }
  };

  const sectionLabel = (role: StaffRoleKey) =>
    role === 'waiter' ? t('analytics:staffSummary.sectionDineIn')
      : role === 'cashier' ? t('analytics:staffSummary.sectionTakeaway')
        : t('analytics:staffSummary.sectionDelivery');

  const getRoleIcon = (role: string, size = 'w-5 h-5') => {
    switch (role) {
      case 'driver': return <Truck className={`${size} text-blue-600 dark:text-blue-400`} />;
      case 'waiter': return <Utensils className={`${size} text-purple-600 dark:text-purple-400`} />;
      case 'cashier': return <ShoppingBag className={`${size} text-emerald-600 dark:text-emerald-400`} />;
      default: return <User className={`${size} text-t3`} />;
    }
  };

  const getRoleBgColor = (role: string) => {
    switch (role) {
      case 'driver': return 'bg-blue-500/20';
      case 'waiter': return 'bg-purple-500/20';
      case 'cashier': return 'bg-emerald-500/20';
      default: return 'bg-surface-3 dark:bg-surface-3/60';
    }
  };

  // UI display version — translated; the thermal print keeps its own Greek labels.
  const getPaymentDisplayLabel = (type: string) => {
    const lower = type?.toLowerCase() || '';
    if (lower === 'cash') return t('analytics:staffSummary.paymentCash');
    if (lower === 'card' || lower === 'pos') return `${t('analytics:staffSummary.paymentCard')} / POS`;
    if (lower === 'iris') return 'IRIS';
    if (lower === 'credit') return t('common:payment.credit');
    if (PREPAID_TYPES.includes(lower)) return t('analytics:staffSummary.paymentPrepaid');
    return type;
  };

  const getPaymentTypeColor = (type: string) => {
    const lower = type?.toLowerCase() || '';
    if (lower === 'cash') return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40';
    if (lower === 'card' || lower === 'pos') return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40';
    if (lower === 'iris') return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/40';
    if (lower === 'credit') return 'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40';
    if (PREPAID_TYPES.includes(lower)) return 'text-t3 bg-surface-3 dark:bg-surface-2/80';
    return 'text-t3 bg-surface-3 dark:bg-surface-2';
  };

  const sendPrint = async (cmds: PrintCmd[]) => {
    const response = await fetch(`${API_BASE_URL}/api/stores/${storeId}/printers/print-custom`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
      },
      body: JSON.stringify({ printCommands: cmds })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      alert(t('analytics:staffSummary.printError', { error: err.error || t('common:unknownError', 'Unknown error') }));
    }
  };

  // Συνοπτική / αναλυτική εκτύπωση όλων — αμετάβλητη.
  const handlePrint = async (type: 'summary' | 'detailed') => {
    if (!data || !storeId) return;
    setShowPrintMenu(false);

    const businessDate = formatTime(data.businessDayStart);
    const now = new Date().toLocaleString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const euro = (n: number) => `${n.toFixed(2).replace('.', ',')}`;

    // «Χωρίς οδηγό» is store money too (delivery χωρίς ανάθεση) — μπαίνει στο
    // γενικό σύνολο, αλλιώς το report δεν ταυτίζεται με το Ζ.
    const nd = data.noDriver && data.noDriver.totalOrders > 0 ? data.noDriver : null;
    const allCash = (data.totals.drivers?.cashTotal || 0) + (data.totals.waiters?.cashTotal || 0) + (data.totals.cashiers?.cashTotal || 0) + (nd?.cashTotal || 0);
    const allCard = (data.totals.drivers?.posTotal || 0) + (data.totals.waiters?.cardTotal || 0) + (data.totals.cashiers?.cardTotal || 0) + (nd?.posTotal || 0);
    const allIris = (data.totals.waiters?.irisTotal || 0) + (data.totals.cashiers?.irisTotal || 0) + (nd?.irisTotal || 0);
    const waitersCredit = creditTotalOf(data.totals.waiters, data.waiters);
    const cashiersCredit = creditTotalOf(data.totals.cashiers, data.cashiers);
    const allCredit = waitersCredit + cashiersCredit;
    const allTotal = (data.totals.drivers?.grandTotal || 0) + (data.totals.waiters?.grandTotal || 0) + (data.totals.cashiers?.grandTotal || 0) + (nd?.grandTotal || 0);

    const cmds: PrintCmd[] = [];

    const addRow = (label: string, value: string, bold = false) => {
      const kind = bold ? 'bold' : 'text';
      cmds.push({ type: kind, content: `${label}${value.padStart(42 - label.length)}` });
    };

    // Header
    cmds.push({ type: 'bold_center', content: 'ΤΑΜΕΙΑ ΠΡΟΣΩΠΙΚΟΥ' });
    cmds.push({ type: 'center', content: storeName });
    cmds.push({ type: 'center', content: businessDate });
    cmds.push({ type: 'center', content: `Εκτ: ${now}` });
    cmds.push({ type: 'divider', content: '' });

    // Group section helper
    const addGroup = (title: string, count: number, cash: number, card: number, iris: number | null, total: number, discount?: number, trainingCash?: number, credit?: number) => {
      if (count === 0) return;
      cmds.push({ type: 'bold', content: `${title} (${count})` });
      if (cash > 0) addRow('  Μετρητα:', `${euro(cash)}`);
      if (trainingCash && trainingCash > 0) addRow('  Εκπαιδ. Μετρ.:', `${euro(trainingCash)}`);
      if (card > 0) addRow('  Καρτα/POS:', `${euro(card)}`);
      if (iris !== null && iris > 0) addRow('  IRIS:', `${euro(iris)}`);
      if (credit && credit > 0) addRow('  Επι πιστωσει:', `${euro(credit)}`);
      if (discount && discount > 0) addRow('  Εκπτωσεις:', `-${euro(discount)}`);
      addRow('  Συνολο:', `${euro(total)}`, true);
      cmds.push({ type: 'divider', content: '' });
    };

    const cashiersTrainingCash = includeTraining
      ? data.cashiers.reduce((s, c) => s + (c.cashTrainingTotal || 0), 0)
      : 0;

    // Helper: platform rows for a driver or driver totals object
    const addPlatformRows = (d: {
      efoodTotal?: number; woltTotal?: number; boxTotal?: number; ownTotal?: number;
      efoodCash?: number; efoodPos?: number; efoodPrepaid?: number;
      woltCash?: number; woltPos?: number; woltPrepaid?: number;
      boxCash?: number; boxPos?: number; boxPrepaid?: number;
      ownCash?: number; ownPos?: number; ownPrepaid?: number;
    }) => {
      const platforms = [
        { label: 'Efood:', val: d.efoodTotal || 0, cash: d.efoodCash || 0, pos: d.efoodPos || 0, prepaid: d.efoodPrepaid || 0 },
        { label: 'Wolt:', val: d.woltTotal || 0, cash: d.woltCash || 0, pos: d.woltPos || 0, prepaid: d.woltPrepaid || 0 },
        { label: 'Box:', val: d.boxTotal || 0, cash: d.boxCash || 0, pos: d.boxPos || 0, prepaid: d.boxPrepaid || 0 },
        { label: 'Ιδια αποστ.:', val: d.ownTotal || 0, cash: d.ownCash || 0, pos: d.ownPos || 0, prepaid: d.ownPrepaid || 0 },
      ].filter(p => p.val > 0.001);
      if (platforms.length === 0) return;
      cmds.push({ type: 'bold', content: '  -- ανα πλατφορμα --' });
      platforms.forEach(p => {
        addRow(`  ${p.label}`, euro(p.val), true);
        if (p.cash > 0.001) addRow('    Μετρητα:', euro(p.cash));
        if (p.pos > 0.001) addRow('    POS:', euro(p.pos));
        if (p.prepaid > 0.001) addRow('    Prepaid:', euro(p.prepaid));
        const other = Math.round((p.val - p.cash - p.pos - p.prepaid) * 100) / 100;
        if (other > 0.005) addRow('    Αγνωστο:', euro(other));
      });
    };

    if (data.drivers.length > 0) {
      addGroup('ΟΔΗΓΟΙ', data.drivers.length, data.totals.drivers?.cashTotal || 0, data.totals.drivers?.posTotal || 0, null, data.totals.drivers?.grandTotal || 0);
      addPlatformRows(data.totals.drivers || {});
      if ((data.totals.drivers?.efoodTotal || 0) + (data.totals.drivers?.woltTotal || 0) + (data.totals.drivers?.boxTotal || 0) + (data.totals.drivers?.ownTotal || 0) > 0) {
        cmds.push({ type: 'divider', content: '' });
      }
    }
    addGroup('ΣΕΡΒΙΤΟΡΟΙ', data.waiters.length, data.totals.waiters?.cashTotal || 0, data.totals.waiters?.cardTotal || 0, data.totals.waiters?.irisTotal || 0, data.totals.waiters?.grandTotal || 0, data.totals.waiters?.discountTotal, undefined, waitersCredit);
    addGroup('ΤΑΜΙΕΣ', data.cashiers.length, data.totals.cashiers?.cashTotal || 0, data.totals.cashiers?.cardTotal || 0, data.totals.cashiers?.irisTotal || 0, data.totals.cashiers?.grandTotal || 0, data.totals.cashiers?.discountTotal, cashiersTrainingCash || undefined, cashiersCredit);

    // Παραδόσεις χωρίς ανατεθειμένο οδηγό — γραμμή ελέγχου (ιδανικά 0).
    if (nd) {
      cmds.push({ type: 'bold', content: `ΧΩΡΙΣ ΟΔΗΓΟ (${nd.totalOrders})` });
      if (nd.cashTotal > 0.001) addRow('  Μετρητα:', euro(nd.cashTotal));
      if (nd.posTotal > 0.001) addRow('  Καρτα/POS:', euro(nd.posTotal));
      if (nd.irisTotal > 0.001) addRow('  IRIS:', euro(nd.irisTotal));
      if (nd.prepaidTotal > 0.001) addRow('  Prepaid:', euro(nd.prepaidTotal));
      addRow('  Συνολο:', euro(nd.grandTotal), true);
      cmds.push({ type: 'text', content: '  * delivery χωρις ανάθεση οδηγου' });
      cmds.push({ type: 'divider', content: '' });
    }

    // Platform-courier deliveries (per platform). Kept separate from the staff
    // grand total — this money is settled by the platform, not held in a drawer.
    const platformDeliveries = data.platformDeliveries || [];
    if (platformDeliveries.length > 0) {
      cmds.push({ type: 'bold', content: 'ΠΛΑΤΦΟΡΜΕΣ (courier)' });
      platformDeliveries.forEach(p => {
        const name = p.platform === 'efood' ? 'Efood' : p.platform === 'wolt' ? 'Wolt' : p.platform === 'box' ? 'Box' : p.platform;
        addRow(`  ${name} (${p.totalOrders}):`, euro(p.grandTotal), true);
        if (p.prepaidTotal > 0.001) addRow('    Prepaid:', euro(p.prepaidTotal));
        if (p.cashTotal > 0.001) addRow('    Μετρητα (courier):', euro(p.cashTotal));
        if (p.posTotal > 0.001) addRow('    POS:', euro(p.posTotal));
      });
      const pt = data.totals.platformDeliveries;
      if (pt) addRow('  Συνολο πλατφορμων:', euro(pt.grandTotal), true);
      cmds.push({ type: 'divider', content: '' });
    }

    cmds.push({ type: 'divider', content: '' });
    addRow('ΓΕΝΙΚΟ ΣΥΝΟΛΟ:', `${euro(allTotal)}`, true);
    if (allCash > 0) addRow('  Μετρητα:', `${euro(allCash)}`);
    if (allCard > 0) addRow('  Καρτα/POS:', `${euro(allCard)}`);
    if (allIris > 0) addRow('  IRIS:', `${euro(allIris)}`);
    if (allCredit > 0) addRow('  Επι πιστωσει:', `${euro(allCredit)}`);

    // Detailed per-person
    if (type === 'detailed') {
      const addMembers = (members: any[], isDriver: boolean) => {
        members.forEach(m => {
          cmds.push({ type: 'divider', content: '' });
          cmds.push({ type: 'bold', content: m.name });
          if (isDriver) {
            if (m.cashTotal > 0) addRow('  Μετρητα:', `${euro(m.cashTotal)}`);
            if (m.posTotal > 0) addRow('  POS:', `${euro(m.posTotal)}`);
            if (m.prepaidTotal > 0) addRow('  Prepaid:', `${euro(m.prepaidTotal)}`);
            addRow('  Συνολο:', `${euro(m.grandTotal)}`, true);
            addPlatformRows(m);
          } else {
            if (m.cashTotal > 0) addRow('  Μετρητα:', `${euro(m.cashTotal)}`);
            if (includeTraining && (m.cashTrainingTotal || 0) > 0) addRow('  Εκπαιδ. Μετρ.:', `${euro(m.cashTrainingTotal)}`);
            if (m.cardTotal > 0) addRow('  Καρτα:', `${euro(m.cardTotal)}`);
            if (m.irisTotal > 0) addRow('  IRIS:', `${euro(m.irisTotal)}`);
            if ((m.creditTotal || 0) > 0) addRow('  Επι πιστωσει:', `${euro(m.creditTotal)}`);
            if ((m.discountTotal || 0) > 0) addRow('  Εκπτωσεις:', `-${euro(m.discountTotal)}`);
            addRow('  Συνολο:', `${euro(m.grandTotal)}`, true);
          }
        });
      };
      if (data.drivers.length > 0) { cmds.push({ type: 'bold_center', content: '--- ΟΔΗΓΟΙ ---' }); addMembers(data.drivers, true); }
      if (data.waiters.length > 0) { cmds.push({ type: 'bold_center', content: '--- ΣΕΡΒΙΤΟΡΟΙ ---' }); addMembers(data.waiters, false); }
      if (data.cashiers.length > 0) { cmds.push({ type: 'bold_center', content: '--- ΤΑΜΙΕΣ ---' }); addMembers(data.cashiers, false); }
    }

    cmds.push({ type: 'feed', content: '' });
    cmds.push({ type: 'cut', content: '' });

    try {
      await sendPrint(cmds);
    } catch (e) {
      alert(t('analytics:staffSummary.connectionError'));
    }
  };

  // Χαρτάκι ενός ατόμου: οι κινήσεις κάθε ρόλου του + «ΕΝΕΡΓΕΙΕΣ».
  const handlePrintPerson = async (person: StaffPerson) => {
    if (!storeId) return;
    const key = `emp-${person.id}`;
    setPrintingKey(key);
    try {
      const moneyRoles = ROLE_ORDER.filter(r => person[r]);
      const lists = await Promise.all(moneyRoles.map(role =>
        api.get(`/orders/${storeId}/staff/${person.id}/orders?role=${role}`)
          .then(response => (response.data?.orders || []) as StaffOrder[])));
      const now = new Date().toLocaleString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      const cmds = buildPersonPrintCommands({
        name: person.name,
        storeName,
        now,
        roleLabels: (moneyRoles.length > 0 ? moneyRoles : person.roles).map(getRoleLabel),
        sections: moneyRoles.map((role, i) => ({ role, member: person[role] as StaffMember | DriverMember, orders: lists[i] })),
        user: person.user,
        includeTraining,
        formatOrderTime,
      });
      await sendPrint(cmds);
    } catch (e) {
      alert(t('analytics:staffSummary.connectionError'));
    } finally {
      setPrintingKey(null);
    }
  };

  // Κινήσεις ενός ρόλου του ατόμου (ένα ανοιχτό τη φορά, όπως πριν).
  const toggleOrders = async (staffId: number, role: StaffRoleKey) => {
    if (!storeId) return;
    const key = `${role}-${staffId}`;
    if (expandedKey === key) {
      ordersReq.current += 1;
      setExpandedKey(null);
      setStaffOrders([]);
      setLoadingOrders(false);
      return;
    }
    const req = ++ordersReq.current;
    setLoadingOrders(true);
    setExpandedKey(key);
    setStaffOrders([]);
    try {
      const response = await api.get(`/orders/${storeId}/staff/${staffId}/orders?role=${role}`);
      if (req !== ordersReq.current) return;
      setStaffOrders(response.data?.orders || []);
    } catch (err: any) {
      if (req !== ordersReq.current) return;
      console.error('Error fetching staff orders:', err);
      setStaffOrders([]);
    } finally {
      if (req === ordersReq.current) setLoadingOrders(false);
    }
  };

  // ── Απόδοση ──────────────────────────────────────────────────────────────────────────────────────

  const renderOrdersList = () => {
    if (loadingOrders) {
      return (
        <div className="flex items-center justify-center py-4">
          <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      );
    }

    // When training mode is OFF, hide training orders entirely
    const displayOrders = staffOrders.filter(o => includeTraining || !o.is_training);

    if (displayOrders.length === 0) {
      return (
        <div className="text-center py-4 text-t4 text-sm">
          {t('analytics:staffSummary.noOrders')}
        </div>
      );
    }

    const platformLabel = (p?: string) => {
      if (!p) return null;
      if (p === 'efood') return { text: 'e-food', cls: 'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/50' };
      if (p === 'wolt') return { text: 'Wolt', cls: 'text-cyan-700 dark:text-cyan-300 bg-cyan-100 dark:bg-cyan-900/50' };
      if (p === 'box') return { text: 'Box', cls: 'text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/50' };
      return { text: p, cls: 'text-t3 bg-surface-2' };
    };

    return (
      <div data-testid="staff-orders" className="mt-1 space-y-1.5 border-t border-line-strong/50 pt-3">
        {displayOrders.map((order) => {
          const plat = platformLabel(order.platform_name);
          const isTraining = order.is_training;
          const discount = Number(order.discount_amount) || 0;
          return (
            <div
              key={order.id}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm ${
                isTraining ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/30' : 'bg-surface-2/80'
              }`}
            >
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <div className="flex items-center gap-1 text-t4 whitespace-nowrap">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{formatOrderTime(order.timestamp)}</span>
                </div>
                {order.order_code && (
                  <span className="font-medium text-t1">#{order.order_code}</span>
                )}
                {order.table_number && (
                  <span className="text-purple-600 dark:text-purple-400 font-medium">{t('analytics:staffSummary.tableShort')} {order.table_number}</span>
                )}
                {plat && (
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${plat.cls}`}>
                    {plat.text}
                  </span>
                )}
                {order.customer_name && (
                  <span className="text-t3">{order.customer_name}</span>
                )}
                {isTraining && (
                  <span className="text-xs text-amber-700 dark:text-amber-400 font-semibold">{t('analytics:staffSummary.trainingBadge')}</span>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-auto">
                <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${getPaymentTypeColor(order.payment_type)}`}>
                  {getPaymentDisplayLabel(order.payment_type)}
                </span>
                <div className="text-right">
                  <span className={`font-semibold tabular-nums ${isTraining ? 'text-amber-800 dark:text-amber-300' : 'text-t1'}`}>
                    {formatCurrency(order.amount)}
                  </span>
                  {discount > 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400">-{formatCurrency(discount)}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Κεφαλίδα υπο-ενότητας ρόλου: πατιέται για τις κινήσεις (≥48px για αφή).
  const renderSectionToggle = (staffId: number, role: StaffRoleKey, count: number, unit: string, amount: React.ReactNode) => {
    const isExpanded = expandedKey === `${role}-${staffId}`;
    return (
      <button
        type="button"
        onClick={() => toggleOrders(staffId, role)}
        aria-expanded={isExpanded}
        className="w-full min-h-[48px] px-3 flex items-center gap-2 text-left rounded-xl transition-colors hover:bg-surface-3/40 active:bg-surface-3/60"
      >
        {getRoleIcon(role, 'w-4 h-4')}
        <span className="min-w-0 flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold text-t1">{sectionLabel(role)}</span>
          <span className="text-xs text-t3 whitespace-nowrap">· {count} {unit}</span>
        </span>
        <span className="ml-auto shrink-0 flex items-center gap-2">
          {amount}
          {isExpanded ? <ChevronUp className="w-5 h-5 text-t3" /> : <ChevronDown className="w-5 h-5 text-t4" />}
        </span>
      </button>
    );
  };

  // Τραπέζια (σερβιτόρος) / Take Away (ταμίας): μετρητά, κάρτα, IRIS, «Επί πιστώσει».
  const renderStaffSection = (p: StaffPerson, role: 'waiter' | 'cashier', staff: StaffMember, multi: boolean) => {
    const isExpanded = expandedKey === `${role}-${p.id}`;
    const unit = role === 'waiter' ? t('analytics:staffSummary.paymentsLabel') : t('analytics:staffSummary.ordersLabel');
    const hasCredit = (staff.creditTotal || 0) > 0;
    const amount = multi ? (
      <span className="text-sm font-bold text-t1 tabular-nums">{formatCurrency(staff.grandTotal)}</span>
    ) : null;

    return (
      <section key={`${role}-${p.id}`} data-testid={`staff-section-${role}`} className="rounded-xl border border-line-strong/40 bg-surface-1/60 dark:bg-surface-1/30">
        {renderSectionToggle(p.id, role, staff.totalOrders, unit, amount)}
        <div className="px-3 pb-3 space-y-2">
          <div className={TILE_GRID}>
            <div className="rounded-lg px-3 py-2 bg-green-50 dark:bg-green-900/30">
              <p className="flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400">
                <Banknote className="w-4 h-4" />{t('analytics:staffSummary.paymentCash')}
              </p>
              <p className="font-semibold tabular-nums text-green-800 dark:text-green-300">
                {formatCurrency(includeTraining
                  ? (staff.cashTotal || 0) + (staff.cashTrainingTotal || 0)
                  : staff.cashTotal)}
              </p>
              <p className="text-xs text-green-600 dark:text-green-500">
                {includeTraining
                  ? (staff.cashOrders || 0) + (staff.cashTrainingOrders || 0)
                  : staff.cashOrders} {unit}
              </p>
              {includeTraining && (staff.cashTrainingTotal || 0) > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  {t('analytics:staffSummary.trainingShort')} {formatCurrency(staff.cashTrainingTotal || 0)}
                </p>
              )}
            </div>
            <div className="rounded-lg px-3 py-2 bg-blue-50 dark:bg-blue-900/30">
              <p className="flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-400">
                <CreditCard className="w-4 h-4" />{t('analytics:staffSummary.paymentCard')}
              </p>
              <p className="font-semibold tabular-nums text-blue-800 dark:text-blue-300">{formatCurrency(staff.cardTotal)}</p>
              <p className="text-xs text-blue-600 dark:text-blue-500">{staff.cardOrders} {unit}</p>
              {includeTraining && (staff.cardTrainingTotal || 0) > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">{t('analytics:staffSummary.trainingShort')} {formatCurrency(staff.cardTrainingTotal!)}</p>
              )}
            </div>
            {staff.irisTotal > 0 && (
              <div className="rounded-lg px-3 py-2 bg-purple-50 dark:bg-purple-900/30">
                <p className="flex items-center gap-1.5 text-xs font-medium text-purple-700 dark:text-purple-400">
                  <Smartphone className="w-4 h-4" />IRIS
                </p>
                <p className="font-semibold tabular-nums text-purple-800 dark:text-purple-300">{formatCurrency(staff.irisTotal)}</p>
                <p className="text-xs text-purple-600 dark:text-purple-500">{staff.irisOrders} {unit}</p>
              </div>
            )}
            {/* «Επί πιστώσει» (κωδ. 5): πωλήθηκε, αλλά δεν εισπράχθηκε ακόμη */}
            {hasCredit && (
              <div data-testid="staff-credit" className="rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/30">
                <p className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                  <HandCoins className="w-4 h-4" />{t('common:payment.credit')}
                </p>
                <p className="font-semibold tabular-nums text-amber-800 dark:text-amber-300">{formatCurrency(staff.creditTotal || 0)}</p>
                <p className="text-xs text-amber-600 dark:text-amber-500">{staff.creditOrders || 0} {unit}</p>
              </div>
            )}
          </div>

          {/* Έκπτωση πάνω σε ό,τι εισέπραξε — ΔΕΝ είναι οι «Εκπτώσεις που έκανε» των «Ενεργειών». */}
          {(staff.discountTotal || 0) > 0 && (
            <div data-testid="staff-collected-discount" className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700/30 rounded-lg px-3 py-1.5">
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">{t('analytics:staffSummary.collectedDiscount')}:</span>
              <span className="text-xs text-red-700 dark:text-red-300 font-semibold tabular-nums">-{formatCurrency(staff.discountTotal!)}</span>
              {(staff.discountOrders || 0) > 0 && (
                <span className="text-xs text-red-500 ml-auto">{staff.discountOrders} {t('analytics:staffSummary.ordersLabel')}</span>
              )}
            </div>
          )}

          {isExpanded && renderOrdersList()}
        </div>
      </section>
    );
  };

  // Διανομές: προπληρωμένα (δεν τα κρατά ο διανομέας), μετρητά, POS — και «Προς είσπραξη».
  const renderDriverSection = (p: StaffPerson, driver: DriverMember, multi: boolean) => {
    const isExpanded = expandedKey === `driver-${p.id}`;
    const unit = t('analytics:staffSummary.ordersLabel');
    const unknown = Math.round((driver.grandTotal - driver.prepaidTotal - driver.cashTotal - driver.posTotal) * 100) / 100;
    const amount = multi ? (
      <span className="flex flex-col items-end leading-tight">
        <span className="text-[11px] text-t4">{t('analytics:staffSummary.toCollect')}</span>
        <span className="text-sm font-bold text-orange-600 dark:text-orange-400 tabular-nums">{formatCurrency(driver.toCollectTotal)}</span>
      </span>
    ) : null;

    return (
      <section key={`driver-${p.id}`} data-testid="staff-section-driver" className="rounded-xl border border-line-strong/40 bg-surface-1/60 dark:bg-surface-1/30">
        {renderSectionToggle(p.id, 'driver', driver.totalOrders, unit, amount)}
        <div className="px-3 pb-3 space-y-2">
          <div className={TILE_GRID}>
            {/* Prepaid - driver doesn't collect */}
            <div className="rounded-lg px-3 py-2 bg-surface-2 dark:bg-surface-2/60">
              <p className="flex items-center gap-1.5 text-xs font-medium text-t4">
                <Globe className="w-4 h-4" />{t('analytics:staffSummary.paymentPrepaid')}
              </p>
              <p className="font-semibold tabular-nums text-t2">{formatCurrency(driver.prepaidTotal)}</p>
              <p className="text-xs text-t4">{driver.prepaidOrders} {unit}</p>
            </div>
            {/* Cash - driver collects */}
            <div className="rounded-lg px-3 py-2 bg-green-50 dark:bg-green-900/30">
              <p className="flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400">
                <Banknote className="w-4 h-4" />{t('analytics:staffSummary.paymentCash')}
              </p>
              <p className="font-semibold tabular-nums text-green-800 dark:text-green-300">{formatCurrency(driver.cashTotal)}</p>
              <p className="text-xs text-green-600 dark:text-green-500">{driver.cashOrders} {unit}</p>
            </div>
            {/* POS - driver collects via terminal */}
            <div className="rounded-lg px-3 py-2 bg-blue-50 dark:bg-blue-900/30">
              <p className="flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-400">
                <Smartphone className="w-4 h-4" />POS
              </p>
              <p className="font-semibold tabular-nums text-blue-800 dark:text-blue-300">{formatCurrency(driver.posTotal)}</p>
              <p className="text-xs text-blue-600 dark:text-blue-500">{driver.posOrders} {unit}</p>
            </div>
          </div>

          {/* Unknown payment type warning — shows when grand_total ≠ prepaid+cash+pos */}
          {unknown > 0.005 && (
            <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/30 rounded-lg px-3 py-1.5">
              <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">{t('analytics:staffSummary.unknownPaymentType')}</span>
              <span className="text-xs text-amber-800 dark:text-amber-300 font-semibold tabular-nums">{formatCurrency(unknown)}</span>
            </div>
          )}

          {isExpanded && renderOrdersList()}
        </div>
      </section>
    );
  };

  // «Ενέργειες» από την αναφορά ταμείου — μόνο οι μη μηδενικές γραμμές, και οι γύροι όταν υπάρχουν.
  const renderActions = (u?: UserRow | null, testId = 'staff-actions') => {
    const rows = actionRowsOf(u);
    const rounds = roundsOf(u);
    if (rows.length === 0 && rounds <= 0) return null;
    return (
      <div data-testid={testId} className="rounded-xl border border-line-strong/40 px-3 py-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-t4 py-1">{t('analytics:staffSummary.actionsTitle')}</p>
        <ul className="divide-y divide-line-strong/30">
          {rows.map(r => {
            const meta = ACTION_META[r.key];
            const Icon = meta.icon;
            return (
              <li key={r.key} data-testid={`staff-action-${r.key}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="flex items-center gap-2 text-t2 min-w-0">
                  <Icon className={`w-4 h-4 shrink-0 ${meta.cls}`} />
                  <span>{t(meta.labelKey, { n: r.count })}</span>
                </span>
                <span className="font-semibold text-t1 tabular-nums shrink-0">{formatCurrency(r.value)}</span>
              </li>
            );
          })}
          {rounds > 0 && (
            <li data-testid="staff-action-rounds" className="flex items-center gap-2 py-2 text-sm text-t2">
              <ListOrdered className="w-4 h-4 shrink-0 text-blue-600 dark:text-blue-400" />
              <span>{t('analytics:staffSummary.actionRounds', { n: rounds })}</span>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const renderPersonCard = (p: StaffPerson) => {
    const moneyRoles = ROLE_ORDER.filter(r => p[r]);
    const multi = moneyRoles.length > 1;
    const staffRows = [p.waiter, p.cashier].filter((m): m is StaffMember => !!m);
    const driverOnly = !!p.driver && staffRows.length === 0;
    // Κεφαλίδα: διανομέας μόνο → «Προς Είσπραξη» (όπως πάντα)· αλλιώς «Σύνολο» = άθροισμα των ρόλων του.
    const total = staffRows.reduce((s, m) => s + (Number(m.grandTotal) || 0), 0) + (Number(p.driver?.grandTotal) || 0);
    const training = staffRows.reduce((s, m) => s + (Number(m.grandTrainingTotal) || 0), 0);
    const zCollected = moneyRoles.length === 0 ? collectedOf(p.user) : 0;
    const avatarRole = p.roles.length === 1 ? p.roles[0] : null;
    const printKey = `emp-${p.id}`;

    return (
      <article
        key={printKey}
        data-testid="staff-person"
        data-employee-id={p.id}
        className="bg-surface-2 dark:bg-surface-2/50 rounded-2xl p-4 border border-line-strong/50 flex flex-col gap-3"
      >
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-full shrink-0 flex items-center justify-center ${avatarRole ? getRoleBgColor(avatarRole) : getRoleBgColor('')}`}>
            {avatarRole ? getRoleIcon(avatarRole) : <User className="w-5 h-5 text-t3" />}
          </div>
          {/* Όνομα αριστερά, ποσό δεξιά· σε στενή κάρτα το ποσό πέφτει κάτω από το όνομα. */}
          <div className="flex-1 min-w-0 flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
            <div className="min-w-0">
              <h3 className="font-semibold text-t1 leading-tight break-words">{p.name}</h3>
              {p.roles.length > 0 && (
                <p className="text-sm text-t3 mt-0.5">{p.roles.map(getRoleLabel).join(' · ')}</p>
              )}
            </div>
            <div className="text-right ml-auto">
              {moneyRoles.length > 0 ? (
                <>
                  <p className="text-xs text-t4">{driverOnly ? t('analytics:staffSummary.toCollect') : t('analytics:staffSummary.personTotal')}</p>
                  <p data-testid="staff-person-total" className={`text-xl font-bold tabular-nums whitespace-nowrap ${driverOnly ? 'text-orange-600 dark:text-orange-400' : 'text-t1'}`}>
                    {formatCurrency(driverOnly ? p.driver!.toCollectTotal : total)}
                  </p>
                  {includeTraining && training > 0 && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">+{formatCurrency(training)} {t('analytics:staffSummary.trainingShort')}</p>
                  )}
                </>
              ) : Math.abs(zCollected) > 0.005 ? (
                // Εισπράξεις που το Ζ δίνει σε αυτόν αλλά δεν φαίνονται στις πληρωμές προσωπικού — να μη χαθούν.
                <>
                  <p className="text-xs text-amber-700 dark:text-amber-400">{t('analytics:staffSummary.zCollected')}</p>
                  <p data-testid="staff-person-z" className="text-lg font-bold tabular-nums whitespace-nowrap text-amber-800 dark:text-amber-300">{formatCurrency(zCollected)}</p>
                </>
              ) : (
                <p className="text-xs text-t4 pt-1">{t('analytics:staffSummary.noCollections')}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => handlePrintPerson(p)}
            disabled={printingKey === printKey}
            className="w-11 h-11 shrink-0 rounded-xl bg-surface-3 dark:bg-surface-3/50 hover:bg-surface-3 dark:hover:bg-surface-4/60 flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
            title={t('analytics:staffSummary.printStaffTooltip', { name: p.name })}
            aria-label={t('analytics:staffSummary.printStaffTooltip', { name: p.name })}
          >
            {printingKey === printKey ? (
              <RefreshCw className="w-5 h-5 text-t2 animate-spin" />
            ) : (
              <Printer className="w-5 h-5 text-t2" />
            )}
          </button>
        </div>

        {moneyRoles.map(role => (role === 'driver'
          ? renderDriverSection(p, p.driver!, multi)
          : renderStaffSection(p, role, p[role] as StaffMember, multi)))}

        {renderActions(p.user)}
      </article>
    );
  };

  // Render a platform-delivery card (non-self-delivery: platform's courier
  // delivers, so no staff member holds this money — mostly prepaid).
  const renderPlatformCard = (p: PlatformDelivery) => {
    const meta = p.platform === 'efood' ? { name: 'e-food', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-500/20' }
      : p.platform === 'wolt' ? { name: 'Wolt', color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-500/20' }
        : p.platform === 'box' ? { name: 'Box', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/20' }
          : { name: p.platform, color: 'text-t2', bg: 'bg-surface-2' };
    return (
      <div key={`platform-${p.platform}`} data-testid="staff-platform" className="bg-surface-2 dark:bg-surface-2/50 rounded-2xl p-4 border border-line-strong/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${meta.bg}`}><Bike className={`w-5 h-5 ${meta.color}`} /></div>
            <div>
              <p className={`font-semibold ${meta.color}`}>{meta.name}</p>
              <p className="text-xs text-t4">{p.totalOrders} {t('analytics:staffSummary.ordersLabel')} · {t('analytics:staffSummary.courierDelivery')}</p>
            </div>
          </div>
          <p className="text-lg font-bold text-t1 tabular-nums">{formatCurrency(p.grandTotal)}</p>
        </div>
        <div className={CONTROL_GRID}>
          <div>
            <p className="text-xs text-t4">{t('analytics:staffSummary.paymentPrepaid')}</p>
            <p className="font-semibold text-t2 tabular-nums">{formatCurrency(p.prepaidTotal)}</p>
          </div>
          <div>
            <p className="text-xs text-t4">{t('analytics:staffSummary.paymentCash')}</p>
            <p className="font-semibold text-green-600 dark:text-green-400 tabular-nums">{formatCurrency(p.cashTotal)}</p>
          </div>
          <div>
            <p className="text-xs text-t4">POS</p>
            <p className="font-semibold text-blue-600 dark:text-blue-400 tabular-nums">{formatCurrency(p.posTotal)}</p>
          </div>
        </div>
        {p.cashTotal > 0.001 && (
          <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-2">{t('analytics:staffSummary.courierCollectsCash')}</p>
        )}
      </div>
    );
  };

  // «Χωρίς οδηγό» control card — delivery orders the store settles that never
  // got a driver. Amber because it's an operational miss (ideally 0).
  const renderNoDriverCard = (nd: NoDriverBucket) => (
    <div key="no-driver" data-testid="staff-no-driver" className="bg-amber-500/10 rounded-2xl p-4 border border-amber-500/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20"><Truck className="w-5 h-5 text-amber-700 dark:text-amber-400" /></div>
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-300">{t('analytics:staffSummary.noDriverTitle')}</p>
            <p className="text-xs text-t4">{nd.totalOrders} {t('analytics:staffSummary.ordersLabel')} · {t('analytics:staffSummary.noDriverHint')}</p>
          </div>
        </div>
        <p className="text-lg font-bold text-t1 tabular-nums">{formatCurrency(nd.grandTotal)}</p>
      </div>
      <div className={CONTROL_GRID}>
        <div>
          <p className="text-xs text-t4">{t('analytics:staffSummary.paymentCash')}</p>
          <p className="font-semibold text-green-600 dark:text-green-400 tabular-nums">{formatCurrency(nd.cashTotal)}</p>
        </div>
        <div>
          <p className="text-xs text-t4">POS</p>
          <p className="font-semibold text-blue-600 dark:text-blue-400 tabular-nums">{formatCurrency(nd.posTotal)}</p>
        </div>
        <div>
          <p className="text-xs text-t4">IRIS</p>
          <p className="font-semibold text-purple-600 dark:text-purple-400 tabular-nums">{formatCurrency(nd.irisTotal)}</p>
        </div>
        <div>
          <p className="text-xs text-t4">{t('analytics:staffSummary.paymentPrepaid')}</p>
          <p className="font-semibold text-t2 tabular-nums">{formatCurrency(nd.prepaidTotal)}</p>
        </div>
      </div>
    </div>
  );

  // «Χωρίς χρήστη» (αναφορά ταμείου): εισπράξεις/ενέργειες που δεν έχουν χρήστη — γραμμή ελέγχου.
  const renderNoUserCard = (u: UserRow) => {
    const money = [
      { key: 'cash', label: t('analytics:staffSummary.paymentCash'), value: Number(u.cash) || 0, cls: 'text-green-600 dark:text-green-400' },
      { key: 'card', label: t('analytics:staffSummary.paymentCard'), value: Number(u.card) || 0, cls: 'text-blue-600 dark:text-blue-400' },
      { key: 'iris', label: 'IRIS', value: Number(u.iris) || 0, cls: 'text-purple-600 dark:text-purple-400' },
      { key: 'credit', label: t('common:payment.credit'), value: Number(u.credit) || 0, cls: 'text-amber-700 dark:text-amber-400' },
      { key: 'other', label: t('analytics:staffSummary.paymentOther'), value: Number(u.other) || 0, cls: 'text-t2' },
      { key: 'prepaid', label: t('analytics:staffSummary.paymentPrepaid'), value: Number(u.prepaid) || 0, cls: 'text-t2' },
    ].filter(m => Math.abs(m.value) > 0.005);
    return (
      <div key="no-user" data-testid="staff-no-user" className="bg-amber-500/10 rounded-2xl p-4 border border-amber-500/30 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-amber-500/20"><UserX className="w-5 h-5 text-amber-700 dark:text-amber-400" /></div>
            <div className="min-w-0">
              <p className="font-semibold text-amber-800 dark:text-amber-300">{t('analytics:staffSummary.noUserTitle')}</p>
              <p className="text-xs text-t4">{t('analytics:staffSummary.noUserHint')}</p>
            </div>
          </div>
          <p className="text-lg font-bold text-t1 tabular-nums">{formatCurrency(collectedOf(u))}</p>
        </div>
        {money.length > 0 && (
          <div className={CONTROL_GRID}>
            {money.map(m => (
              <div key={m.key}>
                <p className="text-xs text-t4">{m.label}</p>
                <p className={`font-semibold tabular-nums ${m.cls}`}>{formatCurrency(m.value)}</p>
              </div>
            ))}
          </div>
        )}
        {renderActions(u, 'staff-no-user-actions')}
      </div>
    );
  };

  const renderContent = () => {
    if (!data) return null;

    const platformDeliveries = data.platformDeliveries || [];
    const nd = data.noDriver && data.noDriver.totalOrders > 0 ? data.noDriver : null;
    const showNoDriver = !!nd && (filter === 'all' || filter === 'drivers');
    const showNoUser = !!noUser && filter === 'all';
    const showPlatforms = filter === 'all' && platformDeliveries.length > 0;

    if (visiblePeople.length === 0 && !showNoDriver && !showNoUser && !showPlatforms) {
      return (
        <div className="text-center py-12 text-t4">
          <Package className="w-12 h-12 mx-auto mb-3 text-t4" />
          <p>{t('analytics:staffSummary.noActivity')}</p>
        </div>
      );
    }

    return (
      <>
        {visiblePeople.length > 0 && (
          <div className={CARD_GRID}>
            {visiblePeople.map(renderPersonCard)}
          </div>
        )}
        {(showNoDriver || showNoUser) && (
          <div className={CARD_GRID}>
            {showNoDriver && renderNoDriverCard(nd!)}
            {showNoUser && renderNoUserCard(noUser!)}
          </div>
        )}
        {showPlatforms && (
          <div>
            <p className="text-xs uppercase tracking-wide text-t4 mb-2 px-1">
              {t('analytics:staffSummary.platformsTitle')}
            </p>
            <div className={CARD_GRID}>
              {platformDeliveries.map(renderPlatformCard)}
            </div>
          </div>
        )}
      </>
    );
  };

  // Σύνολα του φίλτρου — τα ίδια νούμερα με το κάτω μέρος των παλιών «Πληρωμών Προσωπικού».
  type Stat = { key: string; label: string; value: string; labelCls: string; valueCls: string; sub?: string; testId?: string };
  const footerStats = (): Stat[] | null => {
    if (!data) return null;
    const muted = { labelCls: 'text-t4', valueCls: 'text-t1' };
    const green = { labelCls: 'text-green-600 dark:text-green-400', valueCls: 'text-green-600 dark:text-green-400' };
    const blue = { labelCls: 'text-blue-600 dark:text-blue-400', valueCls: 'text-blue-600 dark:text-blue-400' };
    const purple = { labelCls: 'text-purple-600 dark:text-purple-400', valueCls: 'text-purple-600 dark:text-purple-400' };
    const amber = { labelCls: 'text-amber-700 dark:text-amber-400', valueCls: 'text-amber-700 dark:text-amber-400' };
    const red = { labelCls: 'text-red-600 dark:text-red-400', valueCls: 'text-red-600 dark:text-red-400' };
    const staffCount = data.drivers.length + data.waiters.length + data.cashiers.length;

    if (filter === 'all' && staffCount > 0) {
      // Ίδια άθροιση με την εκτύπωση: το «Χωρίς οδηγό» είναι λεφτά καταστήματος.
      const nd = data.noDriver && data.noDriver.totalOrders > 0 ? data.noDriver : null;
      const allCash = (data.totals.drivers?.cashTotal || 0) + (data.totals.waiters?.cashTotal || 0) + (data.totals.cashiers?.cashTotal || 0) + (nd?.cashTotal || 0);
      const allCard = (data.totals.drivers?.posTotal || 0) + (data.totals.waiters?.cardTotal || 0) + (data.totals.cashiers?.cardTotal || 0) + (nd?.posTotal || 0);
      const allIris = (data.totals.waiters?.irisTotal || 0) + (data.totals.cashiers?.irisTotal || 0) + (nd?.irisTotal || 0);
      const allCredit = creditTotalOf(data.totals.waiters, data.waiters) + creditTotalOf(data.totals.cashiers, data.cashiers);
      const allTotal = (data.totals.drivers?.grandTotal || 0) + (data.totals.waiters?.grandTotal || 0) + (data.totals.cashiers?.grandTotal || 0) + (nd?.grandTotal || 0);
      const stats: Stat[] = [
        { key: 'cash', label: t('analytics:staffSummary.footerCash'), value: formatCurrency(allCash), ...green },
        { key: 'card', label: t('analytics:staffSummary.footerCard'), value: formatCurrency(allCard), ...blue },
      ];
      if (allIris > 0) stats.push({ key: 'iris', label: 'IRIS', value: formatCurrency(allIris), ...purple });
      if (allCredit > 0) stats.push({ key: 'credit', label: t('common:payment.credit'), value: formatCurrency(allCredit), testId: 'footer-credit', ...amber });
      stats.push({ key: 'total', label: t('analytics:staffSummary.footerTotal'), value: formatCurrency(allTotal), labelCls: 'text-t3', valueCls: 'text-t1' });
      return stats;
    }

    if (filter === 'drivers' && data.drivers.length > 0) {
      const totals = data.totals.drivers;
      return [
        { key: 'orders', label: t('analytics:staffSummary.footerOrders'), value: String(totals.totalOrders), ...muted },
        { key: 'prepaid', label: t('analytics:staffSummary.paymentPrepaid'), value: formatCurrency(totals.prepaidTotal), labelCls: 'text-t4', valueCls: 'text-t3' },
        { key: 'cash', label: t('analytics:staffSummary.paymentCash'), value: formatCurrency(totals.cashTotal), ...green },
        { key: 'pos', label: 'POS', value: formatCurrency(totals.posTotal), ...blue },
        { key: 'toCollect', label: t('analytics:staffSummary.footerToCollect'), value: formatCurrency(totals.toCollectTotal), labelCls: 'text-orange-600 dark:text-orange-400', valueCls: 'text-orange-600 dark:text-orange-400' },
      ];
    }

    if (filter === 'waiters' && data.waiters.length > 0) {
      const totals = data.totals.waiters;
      const credit = creditTotalOf(totals, data.waiters);
      const stats: Stat[] = [
        { key: 'payments', label: t('analytics:staffSummary.footerPayments'), value: String(totals.totalOrders), ...muted },
        { key: 'cash', label: t('analytics:staffSummary.paymentCash'), value: formatCurrency(totals.cashTotal), ...green },
        { key: 'card', label: t('analytics:staffSummary.paymentCard'), value: formatCurrency(totals.cardTotal), ...blue },
      ];
      if (totals.irisTotal > 0) stats.push({ key: 'iris', label: 'IRIS', value: formatCurrency(totals.irisTotal), ...purple });
      if (credit > 0) stats.push({ key: 'credit', label: t('common:payment.credit'), value: formatCurrency(credit), testId: 'footer-credit', ...amber });
      stats.push({ key: 'total', label: t('analytics:staffSummary.footerTotal'), value: formatCurrency(totals.grandTotal), ...muted });
      return stats;
    }

    if (filter === 'cashiers' && data.cashiers.length > 0) {
      const totals = data.totals.cashiers;
      const credit = creditTotalOf(totals, data.cashiers);
      const hasDiscount = (totals.discountTotal || 0) > 0;
      const hasTraining = includeTraining && (totals.grandTrainingTotal || 0) > 0;
      const stats: Stat[] = [
        {
          key: 'orders', label: t('analytics:staffSummary.footerOrders'), value: String(totals.totalOrders), ...muted,
          sub: hasTraining && (totals.trainingOrders || 0) > 0 ? `+${totals.trainingOrders} ${t('analytics:staffSummary.trainingShort')}` : undefined,
        },
        { key: 'cash', label: t('analytics:staffSummary.paymentCash'), value: formatCurrency(totals.cashTotal), ...green },
        { key: 'card', label: t('analytics:staffSummary.paymentCard'), value: formatCurrency(totals.cardTotal), ...blue },
      ];
      if (totals.irisTotal > 0) stats.push({ key: 'iris', label: 'IRIS', value: formatCurrency(totals.irisTotal), ...purple });
      if (credit > 0) stats.push({ key: 'credit', label: t('common:payment.credit'), value: formatCurrency(credit), testId: 'footer-credit', ...amber });
      if (hasDiscount) stats.push({ key: 'discount', label: t('analytics:staffSummary.footerCollectedDiscount'), value: `-${formatCurrency(totals.discountTotal!)}`, ...red });
      if (hasTraining) stats.push({ key: 'training', label: t('analytics:staffSummary.footerTraining'), value: `+${formatCurrency(totals.grandTrainingTotal!)}`, ...amber });
      stats.push({ key: 'total', label: t('analytics:staffSummary.footerTotal'), value: formatCurrency(totals.grandTotal), ...muted });
      return stats;
    }

    return null;
  };

  const renderFooter = () => {
    if (error || !data) return null;
    const stats = footerStats();
    if (!stats) return null;
    return (
      <div data-testid="staff-footer" className="sticky bottom-0 z-10 bg-surface-1 border-t border-line-strong py-3">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-3 text-center">
          {stats.map(s => (
            <div key={s.key} data-testid={s.testId}>
              <p className={`text-xs uppercase font-medium ${s.labelCls}`}>{s.label}</p>
              <p className={`text-xl font-bold tabular-nums ${s.valueCls}`}>{s.value}</p>
              {s.sub && <p className="text-xs text-amber-700 dark:text-amber-400">{s.sub}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const filterMeta: Record<Filter, { label: string; icon?: React.ElementType }> = {
    all: { label: t('analytics:staffSummary.filterAll') },
    cashiers: { label: t('analytics:staffSummary.filterCashiers'), icon: ShoppingBag },
    waiters: { label: t('analytics:staffSummary.filterWaiters'), icon: Utensils },
    drivers: { label: t('analytics:staffSummary.filterDrivers'), icon: Truck },
  };

  return (
    <div data-testid="staff-panel" className="flex flex-col">
      {/* Φίλτρα + εκτύπωση όλων + ανανέωση (5 πατήματα = με εκπαιδευτικές) */}
      <div className="sticky top-0 z-20 bg-surface-1 border-b border-line-strong/60 py-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 overflow-x-auto max-w-full">
          {FILTERS.map(f => {
            const active = filter === f;
            const Icon = filterMeta[f].icon;
            const count = peopleFor(f).length;
            return (
              <button
                key={f}
                type="button"
                data-testid={`staff-filter-${f}`}
                aria-pressed={active}
                onClick={() => setFilter(f)}
                className={`h-11 px-4 rounded-full border text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-all active:scale-95 ${
                  active
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-surface-2 border-line-strong/60 text-t2 hover:bg-surface-3'
                }`}
              >
                {Icon && <Icon className="w-4 h-4" />}
                <span>{filterMeta[f].label}</span>
                {count > 0 && (
                  <span className={`min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-semibold ${active ? 'bg-white/25 text-white' : 'bg-surface-3 text-t2'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {data && (
            <span className="text-xs text-t3 whitespace-nowrap">
              {t('analytics:staffSummary.fromDate', { date: formatTime(data.businessDayStart) })}
            </span>
          )}
          {includeTraining && (
            <span className="text-xs text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-2 py-1 rounded-lg">{t('analytics:staffSummary.trainingIncluded')}</span>
          )}
          <div className="relative" ref={printMenuRef}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowPrintMenu(prev => !prev); }}
              disabled={!data}
              className="w-11 h-11 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center transition-all active:scale-95 disabled:opacity-40"
              title={t('analytics:staffSummary.printTooltip')}
              aria-haspopup="menu"
              aria-expanded={showPrintMenu}
            >
              <Printer className="w-5 h-5 text-t2" />
            </button>
            {showPrintMenu && (
              <div className="absolute right-0 top-12 bg-surface-2 border border-line-strong rounded-xl shadow-xl z-30 min-w-[180px] overflow-hidden">
                <button
                  type="button"
                  onClick={() => handlePrint('summary')}
                  className="w-full text-left px-4 py-3.5 text-sm text-t2 hover:bg-surface-3 transition-colors"
                >
                  {t('analytics:staffSummary.printSummary')}
                </button>
                <button
                  type="button"
                  onClick={() => handlePrint('detailed')}
                  className="w-full text-left px-4 py-3.5 text-sm text-t2 hover:bg-surface-3 transition-colors border-t border-line-strong"
                >
                  {t('analytics:staffSummary.printDetailed')}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleRefreshClick(); }}
            disabled={loading}
            className="w-11 h-11 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center transition-all active:scale-95"
            title={t('common:refresh')}
            aria-label={t('common:refresh')}
          >
            <RefreshCw className={`w-5 h-5 text-t2 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="py-4 space-y-4">
        {loading && !data ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => fetchData(includeTraining)}
              className="cashier-primary min-h-[44px] px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-all active:scale-95"
            >
              {t('analytics:staffSummary.retry')}
            </button>
          </div>
        ) : (
          renderContent()
        )}
      </div>

      {renderFooter()}
    </div>
  );
};

export default StaffPanel;
